import { useEffect, useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useRefData } from '../../lib/refdata';
import type { BudgetCycle, BudgetSubmission, BudgetVarianceRow } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

const NEXT_STAGE: Record<BudgetCycle['status'], string | null> = {
  CIRCULAR_ISSUED: 'MOF_REVIEW',
  MOF_REVIEW: 'BILATERALS',
  BILATERALS: 'CABINET',
  CABINET: 'ASSEMBLY_TABLED',
  ASSEMBLY_TABLED: 'ENACTED',
  ENACTED: null,
};

const DEFAULT_MILESTONES = [
  'Circular issued',
  'MDA submissions due',
  'MoF challenge board',
  'Cabinet review',
  'Tabled in National Assembly',
];

function cycleStorageKey(fyId: string): string {
  return `govfin.budgetPrepCycle.${fyId}`;
}

export function BudgetPrep() {
  const { persona } = useAuth();
  const { fy, votes } = useRefData();
  const [cycleId, setCycleId] = useState<string | null>(null);
  const [pasteId, setPasteId] = useState('');

  useEffect(() => {
    if (fy) setCycleId(localStorage.getItem(cycleStorageKey(fy.id)));
  }, [fy]);

  const isAnalyst = persona?.roles.includes('MOF_BUDGET_ANALYST') ?? false;
  const isMdaOfficer = persona?.roles.includes('MDA_FINANCE_OFFICER') ?? false;
  const isMdaPs = persona?.roles.includes('MDA_PS') ?? false;
  const myVote = votes.find((v) => v.code === persona?.voteCode);

  const cycleQ = useApiQuery<BudgetCycle>(cycleId ? `/api/v1/budget-prep/cycles/${cycleId}` : null);
  const varianceQ = useApiQuery<{ items: BudgetVarianceRow[] }>(cycleId ? `/api/v1/budget-prep/cycles/${cycleId}/variance` : null);

  const voteName = (id: string) => {
    const v = votes.find((x) => x.id === id);
    return v ? `${v.code} — ${v.name}` : id;
  };

  if (!fy) return <ErrorNote message="No OPEN fiscal year — create and open one under Chart of accounts." />;

  if (!cycleId) {
    return (
      <div>
        <PageHeader title="Budget Preparation (PPBB)" subtitle={`Fiscal Year ${fy.year} — call circular, MDA submissions, MoF challenge, enactment`} />
        <div className="grid gap-4 lg:grid-cols-2">
          {isAnalyst ? (
            <OpenCycleCard fyId={fy.id} onOpened={(id) => setCycleId(id)} />
          ) : (
            <div className="card p-5">
              <h2 className="mb-1 text-sm font-bold text-slate-700">No budget cycle open yet</h2>
              <p className="text-sm text-slate-500">The Budget Division opens the call circular for a fiscal year before ministries can submit.</p>
            </div>
          )}
          <div className="card p-5">
            <h2 className="mb-2 text-sm font-bold text-slate-700">Already have a cycle for this year?</h2>
            <p className="mb-3 text-xs text-slate-500">
              There is no lookup-by-year endpoint yet — paste the cycle ID (shown when it was opened) to reattach.
            </p>
            <div className="flex gap-2">
              <input className="field-input flex-1" placeholder="Cycle ID" value={pasteId} onChange={(e) => setPasteId(e.target.value)} />
              <button
                className="btn-secondary"
                onClick={() => {
                  localStorage.setItem(cycleStorageKey(fy.id), pasteId.trim());
                  setCycleId(pasteId.trim());
                }}
                disabled={!pasteId.trim()}
              >
                Open
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const cycle = cycleQ.data;
  const nextStage = cycle ? NEXT_STAGE[cycle.status] : null;
  const mySubmission = cycle?.submissions?.find((s) => s.voteId === myVote?.id) ?? null;

  const advance = async () => {
    if (!cycle) return;
    await api.post(`/api/v1/budget-prep/cycles/${cycle.id}/transition`);
    cycleQ.reload();
  };

  return (
    <div>
      <PageHeader
        title="Budget Preparation (PPBB)"
        subtitle={`Fiscal Year ${fy.year} — call circular, MDA submissions, MoF challenge, enactment`}
        actions={
          cycle && isAnalyst && nextStage ? (
            <button className="btn-primary" onClick={advance}>
              Advance → {nextStage.replaceAll('_', ' ')}
            </button>
          ) : undefined
        }
      />

      {cycleQ.error ? <ErrorNote message={cycleQ.error} /> : null}
      {cycleQ.loading ? (
        <Spinner />
      ) : !cycle ? null : (
        <>
          <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Cycle stage" value={<StatusBadge status={cycle.status} />} hint={cycle.circularDocId ?? 'No circular doc filed'} />
            <StatCard label="Ceilings set" value={cycle.ceilings?.length ?? 0} hint="Votes with an indicative ceiling" />
            <StatCard label="MDA submissions" value={cycle.submissions?.length ?? 0} hint={`${(cycle.submissions ?? []).filter((s) => s.status === 'SUBMITTED').length} signed off`} />
            <StatCard
              label="My ministry"
              value={mySubmission ? <StatusBadge status={mySubmission.status} /> : '—'}
              hint={myVote ? voteName(myVote.id) : 'No vote bound to this persona'}
            />
          </div>

          <div className="mb-6 grid gap-4 lg:grid-cols-2">
            <CeilingsCard cycle={cycle} isAnalyst={isAnalyst} voteName={voteName} votes={votes} onDone={() => cycleQ.reload()} />
            <VarianceCard rows={varianceQ.data?.items ?? []} loading={varianceQ.loading} voteName={voteName} />
          </div>

          {myVote ? (
            <div className="mb-6">
              <h2 className="mb-2 text-sm font-bold text-slate-700">My ministry's submission — {voteName(myVote.id)}</h2>
              <MySubmissionCard
                cycleId={cycle.id}
                voteId={myVote.id}
                submission={mySubmission}
                canDraft={isMdaOfficer}
                canSignOff={isMdaPs}
                onDone={() => cycleQ.reload()}
              />
            </div>
          ) : null}

          {isAnalyst ? (
            <div className="mb-6">
              <h2 className="mb-2 text-sm font-bold text-slate-700">MoF challenge board</h2>
              <ChallengeBoard cycle={cycle} voteName={voteName} onDone={() => cycleQ.reload()} />
            </div>
          ) : null}

          {cycle.status === 'ASSEMBLY_TABLED' || cycle.status === 'ENACTED' ? (
            <BillExportCard cycleId={cycle.id} voteName={voteName} />
          ) : null}
        </>
      )}
    </div>
  );
}

function OpenCycleCard({ fyId, onOpened }: { fyId: string; onOpened: (id: string) => void }) {
  const [circularDocId, setCircularDocId] = useState('');
  const [milestones, setMilestones] = useState(DEFAULT_MILESTONES.map((m) => ({ milestone: m, date: '' })));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const cycle = await api.post<BudgetCycle>('/api/v1/budget-prep/cycles', {
        fiscalYearId: fyId,
        ...(circularDocId ? { circularDocId } : {}),
        calendar: milestones.filter((m) => m.date).map((m) => ({ milestone: m.milestone, date: m.date })),
      });
      localStorage.setItem(cycleStorageKey(fyId), cycle.id);
      onOpened(cycle.id);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to open cycle');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Open the call circular</h2>
      <p className="mb-3 text-xs text-slate-500">Sets the budget calendar every ministry works to for this fiscal year.</p>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <Field label="Circular document reference (optional)" className="mb-3">
        <input className="field-input" value={circularDocId} onChange={(e) => setCircularDocId(e.target.value)} placeholder="e.g. DOCV-MOF-2027-CIRCULAR" />
      </Field>
      <div className="field-label">Calendar milestones</div>
      {milestones.map((m, idx) => (
        <div key={m.milestone} className="mb-2 flex items-center gap-2">
          <span className="w-52 shrink-0 text-xs text-slate-600">{m.milestone}</span>
          <input
            className="field-input"
            type="date"
            value={m.date}
            onChange={(e) => setMilestones(milestones.map((r, i) => (i === idx ? { ...r, date: e.target.value } : r)))}
          />
        </div>
      ))}
      <button className="btn-primary mt-2" onClick={submit} disabled={busy || milestones.every((m) => !m.date)}>
        {busy ? 'Opening…' : 'Open budget cycle'}
      </button>
    </div>
  );
}

function CeilingsCard({
  cycle,
  isAnalyst,
  voteName,
  votes,
  onDone,
}: {
  cycle: BudgetCycle;
  isAnalyst: boolean;
  voteName: (id: string) => string;
  votes: Array<{ id: string; code: string; name: string }>;
  onDone: () => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-700">Indicative ceilings</h2>
        {isAnalyst && cycle.status === 'CIRCULAR_ISSUED' ? (
          <button className="btn-secondary !py-1 text-xs" onClick={() => setShowAdd(true)}>
            + Set ceilings
          </button>
        ) : null}
      </div>
      <div className="card overflow-x-auto">
        {(cycle.ceilings?.length ?? 0) === 0 ? (
          <EmptyState title="No ceilings set" hint="Ceilings can only be set while the circular is open." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Vote</th>
                <th className="table-head">Class</th>
                <th className="table-head text-right">Ceiling</th>
              </tr>
            </thead>
            <tbody>
              {cycle.ceilings!.map((c) => (
                <tr key={c.id}>
                  <td className="table-cell">{voteName(c.voteId)}</td>
                  <td className="table-cell text-xs text-slate-500">{c.econClass ?? 'All classes'}</td>
                  <td className="table-cell text-right tabular-nums">{scr(c.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showAdd ? (
        <CeilingsModal
          cycleId={cycle.id}
          votes={votes}
          onClose={() => setShowAdd(false)}
          onDone={() => {
            setShowAdd(false);
            onDone();
          }}
        />
      ) : null}
    </div>
  );
}

function CeilingsModal({
  cycleId,
  votes,
  onClose,
  onDone,
}: {
  cycleId: string;
  votes: Array<{ id: string; code: string; name: string }>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [rows, setRows] = useState<Array<{ voteId: string; amount: string; econClass: string }>>([
    { voteId: '', amount: '', econClass: '' },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/budget-prep/cycles/${cycleId}/ceilings`, {
        ceilings: rows
          .filter((r) => r.voteId && r.amount)
          .map((r) => ({ voteId: r.voteId, amount: Number.parseFloat(r.amount), ...(r.econClass ? { econClass: r.econClass } : {}) })),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to set ceilings');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Set indicative ceilings" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {rows.map((row, idx) => (
          <div key={idx} className="flex gap-2">
            <select
              className="field-input flex-1"
              value={row.voteId}
              onChange={(e) => setRows(rows.map((r, i) => (i === idx ? { ...r, voteId: e.target.value } : r)))}
            >
              <option value="">Select vote…</option>
              {votes.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.code} — {v.name}
                </option>
              ))}
            </select>
            <input
              className="field-input w-40"
              placeholder="Class (optional)"
              value={row.econClass}
              onChange={(e) => setRows(rows.map((r, i) => (i === idx ? { ...r, econClass: e.target.value } : r)))}
            />
            <input
              className="field-input w-40"
              type="number"
              min="0"
              step="0.01"
              placeholder="Ceiling (SCR)"
              value={row.amount}
              onChange={(e) => setRows(rows.map((r, i) => (i === idx ? { ...r, amount: e.target.value } : r)))}
            />
            <button className="btn-secondary !px-2.5" onClick={() => setRows(rows.filter((_, i) => i !== idx))}>
              ✕
            </button>
          </div>
        ))}
        <button className="btn-secondary !py-1.5 text-xs" onClick={() => setRows([...rows, { voteId: '', amount: '', econClass: '' }])}>
          + Add vote
        </button>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || rows.every((r) => !r.voteId || !r.amount)}>
            {busy ? 'Saving…' : 'Save ceilings'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function VarianceCard({ rows, loading, voteName }: { rows: BudgetVarianceRow[]; loading: boolean; voteName: (id: string) => string }) {
  return (
    <div>
      <h2 className="mb-2 text-sm font-bold text-slate-700">Ceiling vs request vs decided</h2>
      <div className="card overflow-x-auto">
        {loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <EmptyState title="No submissions yet" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Vote</th>
                <th className="table-head text-right">Ceiling</th>
                <th className="table-head text-right">Requested</th>
                <th className="table-head text-right">Decided</th>
                <th className="table-head text-right">Over ceiling</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.voteId}>
                  <td className="table-cell text-xs">{voteName(r.voteId)}</td>
                  <td className="table-cell text-right tabular-nums">{scr(r.ceiling)}</td>
                  <td className="table-cell text-right tabular-nums">{scr(r.requested)}</td>
                  <td className="table-cell text-right tabular-nums">{scr(r.decided)}</td>
                  <td className={`table-cell text-right tabular-nums ${r.overCeiling > 0 ? 'font-semibold text-rose-600' : 'text-slate-400'}`}>
                    {scr(r.overCeiling)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function MySubmissionCard({
  cycleId,
  voteId,
  submission,
  canDraft,
  canSignOff,
  onDone,
}: {
  cycleId: string;
  voteId: string;
  submission: BudgetSubmission | null;
  canDraft: boolean;
  canSignOff: boolean;
  onDone: () => void;
}) {
  const [showStart, setShowStart] = useState(false);
  const [showLines, setShowLines] = useState(false);
  const [showInitiative, setShowInitiative] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitForReview = async () => {
    if (!submission) return;
    setError(null);
    try {
      await api.post(`/api/v1/budget-prep/submissions/${submission.id}/submit`);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Sign-off failed');
    }
  };

  if (!submission) {
    return canDraft ? (
      <div className="card p-5">
        <p className="mb-3 text-sm text-slate-500">No submission started yet for this vote.</p>
        <button className="btn-primary" onClick={() => setShowStart(true)}>
          Start submission
        </button>
        {showStart ? (
          <StartSubmissionModal
            cycleId={cycleId}
            voteId={voteId}
            onClose={() => setShowStart(false)}
            onDone={() => {
              setShowStart(false);
              onDone();
            }}
          />
        ) : null}
      </div>
    ) : (
      <div className="card">
        <EmptyState title="No submission yet" hint="The MDA finance officer starts the submission." />
      </div>
    );
  }

  return (
    <div className="card p-5">
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="mb-3 flex items-center justify-between">
        <StatusBadge status={submission.status} />
        <div className="flex gap-2">
          {canDraft && submission.status === 'DRAFT' ? (
            <button className="btn-secondary !py-1 text-xs" onClick={() => setShowLines(true)}>
              Edit budget lines
            </button>
          ) : null}
          {canDraft && submission.status === 'DRAFT' ? (
            <button className="btn-secondary !py-1 text-xs" onClick={() => setShowInitiative(true)}>
              + Propose initiative
            </button>
          ) : null}
          {canSignOff && submission.status === 'DRAFT' && (submission.lines?.length ?? 0) > 0 ? (
            <button className="btn-success !py-1 text-xs" onClick={submitForReview}>
              Sign off (PS)
            </button>
          ) : null}
        </div>
      </div>
      <p className="mb-3 whitespace-pre-wrap text-sm text-slate-600">{submission.narrative}</p>
      <table className="w-full">
        <thead>
          <tr>
            <th className="table-head">Budget line</th>
            <th className="table-head text-right">Y1</th>
            <th className="table-head text-right">Y2</th>
            <th className="table-head text-right">Y3</th>
            <th className="table-head text-right">Decided</th>
          </tr>
        </thead>
        <tbody>
          {(submission.lines ?? []).map((l) => (
            <tr key={l.id}>
              <td className="table-cell text-xs" title={l.justification}>
                {l.coaCombinationId}
              </td>
              <td className="table-cell text-right tabular-nums">{scr(l.y1Amount)}</td>
              <td className="table-cell text-right tabular-nums">{scr(l.y2Amount)}</td>
              <td className="table-cell text-right tabular-nums">{scr(l.y3Amount)}</td>
              <td className="table-cell text-right tabular-nums">{l.decidedAmount ? scr(l.decidedAmount) : '—'}</td>
            </tr>
          ))}
          {(submission.lines?.length ?? 0) === 0 ? (
            <tr>
              <td colSpan={5} className="table-cell text-center text-slate-400">
                No lines entered yet.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {(submission.initiatives?.length ?? 0) > 0 ? (
        <div className="mt-3">
          <div className="field-label">New initiatives</div>
          {submission.initiatives!.map((i) => (
            <div key={i.id} className="mb-1 flex items-center justify-between rounded-lg border border-slate-200 px-3 py-1.5 text-sm">
              <span>{i.title}</span>
              <span className="flex items-center gap-2 tabular-nums text-xs text-slate-500">
                Y1 {scr(i.costing.y1)} <StatusBadge status={i.status} />
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {showLines ? (
        <LinesModal
          submission={submission}
          onClose={() => setShowLines(false)}
          onDone={() => {
            setShowLines(false);
            onDone();
          }}
        />
      ) : null}
      {showInitiative ? (
        <InitiativeModal
          submissionId={submission.id}
          onClose={() => setShowInitiative(false)}
          onDone={() => {
            setShowInitiative(false);
            onDone();
          }}
        />
      ) : null}
    </div>
  );
}

function StartSubmissionModal({ cycleId, voteId, onClose, onDone }: { cycleId: string; voteId: string; onClose: () => void; onDone: () => void }) {
  const [narrative, setNarrative] = useState('');
  const [kpis, setKpis] = useState([{ objective: '', indicator: '', target: '' }]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/budget-prep/cycles/${cycleId}/submissions`, {
        voteId,
        narrative,
        kpis: kpis.filter((k) => k.objective && k.indicator && k.target),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to start submission');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Start budget submission" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Narrative (PPBB programme performance narrative)">
          <textarea className="field-input" rows={4} value={narrative} onChange={(e) => setNarrative(e.target.value)} />
        </Field>
        <div className="field-label">KPIs</div>
        {kpis.map((k, idx) => (
          <div key={idx} className="mb-2 grid grid-cols-3 gap-2">
            <input className="field-input" placeholder="Objective" value={k.objective} onChange={(e) => setKpis(kpis.map((r, i) => (i === idx ? { ...r, objective: e.target.value } : r)))} />
            <input className="field-input" placeholder="Indicator" value={k.indicator} onChange={(e) => setKpis(kpis.map((r, i) => (i === idx ? { ...r, indicator: e.target.value } : r)))} />
            <input className="field-input" placeholder="Target" value={k.target} onChange={(e) => setKpis(kpis.map((r, i) => (i === idx ? { ...r, target: e.target.value } : r)))} />
          </div>
        ))}
        <button className="btn-secondary !py-1.5 text-xs" onClick={() => setKpis([...kpis, { objective: '', indicator: '', target: '' }])}>
          + Add KPI
        </button>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || narrative.length < 10 || !kpis.some((k) => k.objective && k.indicator && k.target)}>
            {busy ? 'Starting…' : 'Start submission'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function LinesModal({ submission, onClose, onDone }: { submission: BudgetSubmission; onClose: () => void; onDone: () => void }) {
  const [lines, setLines] = useState(
    (submission.lines ?? []).map((l) => ({
      coaCombinationId: l.coaCombinationId,
      y1Amount: l.y1Amount,
      y2Amount: l.y2Amount,
      y3Amount: l.y3Amount,
      justification: l.justification,
    })),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const addLine = () => setLines([...lines, { coaCombinationId: '', y1Amount: '', y2Amount: '', y3Amount: '', justification: '' }]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/v1/budget-prep/submissions/${submission.id}/lines`, {
        lines: lines
          .filter((l) => l.coaCombinationId && l.y1Amount)
          .map((l) => ({
            coaCombinationId: l.coaCombinationId,
            y1Amount: Number.parseFloat(l.y1Amount),
            y2Amount: Number.parseFloat(l.y2Amount || l.y1Amount),
            y3Amount: Number.parseFloat(l.y3Amount || l.y1Amount),
            justification: l.justification,
          })),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to save lines');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Multi-year budget lines" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">Saving replaces the whole line grid for this submission (only while it is DRAFT).</p>
        {lines.map((row, idx) => (
          <div key={idx} className="rounded-lg border border-slate-200 p-3">
            <div className="mb-2 grid grid-cols-4 gap-2">
              <input
                className="field-input col-span-1"
                placeholder="CoA combination ID"
                value={row.coaCombinationId}
                onChange={(e) => setLines(lines.map((r, i) => (i === idx ? { ...r, coaCombinationId: e.target.value } : r)))}
              />
              <input className="field-input" type="number" placeholder="Y1" value={row.y1Amount} onChange={(e) => setLines(lines.map((r, i) => (i === idx ? { ...r, y1Amount: e.target.value } : r)))} />
              <input className="field-input" type="number" placeholder="Y2" value={row.y2Amount} onChange={(e) => setLines(lines.map((r, i) => (i === idx ? { ...r, y2Amount: e.target.value } : r)))} />
              <input className="field-input" type="number" placeholder="Y3" value={row.y3Amount} onChange={(e) => setLines(lines.map((r, i) => (i === idx ? { ...r, y3Amount: e.target.value } : r)))} />
            </div>
            <div className="flex gap-2">
              <input
                className="field-input flex-1"
                placeholder="Justification"
                value={row.justification}
                onChange={(e) => setLines(lines.map((r, i) => (i === idx ? { ...r, justification: e.target.value } : r)))}
              />
              <button className="btn-secondary !px-2.5" onClick={() => setLines(lines.filter((_, i) => i !== idx))}>
                ✕
              </button>
            </div>
          </div>
        ))}
        <button className="btn-secondary !py-1.5 text-xs" onClick={addLine}>
          + Add line
        </button>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || lines.length === 0}>
            {busy ? 'Saving…' : 'Save lines'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function InitiativeModal({ submissionId, onClose, onDone }: { submissionId: string; onClose: () => void; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [y1, setY1] = useState('');
  const [breakdown, setBreakdown] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/budget-prep/submissions/${submissionId}/initiatives`, {
        title,
        costing: { y1: Number.parseFloat(y1), ...(breakdown ? { breakdown } : {}) },
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to propose initiative');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Propose new initiative" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Year 1 cost (SCR)">
          <input className="field-input" type="number" min="0" step="0.01" value={y1} onChange={(e) => setY1(e.target.value)} />
        </Field>
        <Field label="Costing breakdown (optional)">
          <textarea className="field-input" rows={2} value={breakdown} onChange={(e) => setBreakdown(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || title.length < 3 || !y1}>
            {busy ? 'Proposing…' : 'Propose'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ChallengeBoard({ cycle, voteName, onDone }: { cycle: BudgetCycle; voteName: (id: string) => string; onDone: () => void }) {
  const submitted = (cycle.submissions ?? []).filter((s) => s.status === 'SUBMITTED');
  const [decideTarget, setDecideTarget] = useState<{ lineId: string; label: string } | null>(null);
  const [initiativeTarget, setInitiativeTarget] = useState<string | null>(null);

  return (
    <div className="card overflow-x-auto">
      {submitted.length === 0 ? (
        <EmptyState title="No signed-off submissions yet" hint="Submissions appear here once the MDA Principal Secretary signs off." />
      ) : (
        submitted.map((s) => (
          <div key={s.id} className="border-b border-slate-100 p-4 last:border-b-0">
            <div className="mb-2 font-semibold">{voteName(s.voteId)}</div>
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Budget line</th>
                  <th className="table-head text-right">Requested (Y1)</th>
                  <th className="table-head text-right">Decided</th>
                  <th className="table-head">Latest note</th>
                  <th className="table-head" />
                </tr>
              </thead>
              <tbody>
                {(s.lines ?? []).map((l) => (
                  <tr key={l.id}>
                    <td className="table-cell text-xs" title={l.justification}>
                      {l.coaCombinationId}
                    </td>
                    <td className="table-cell text-right tabular-nums">{scr(l.y1Amount)}</td>
                    <td className="table-cell text-right tabular-nums">{l.decidedAmount ? scr(l.decidedAmount) : '—'}</td>
                    <td className="table-cell text-xs text-slate-500">
                      {(l.notes?.length ?? 0) > 0 ? `${l.notes![l.notes!.length - 1]!.decision}: ${l.notes![l.notes!.length - 1]!.note}` : '—'}
                    </td>
                    <td className="table-cell text-right">
                      <button
                        className="btn-secondary !px-2.5 !py-1 text-xs"
                        onClick={() => setDecideTarget({ lineId: l.id, label: l.coaCombinationId })}
                      >
                        Decide
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(s.initiatives ?? []).filter((i) => i.status === 'PROPOSED').length > 0 ? (
              <div className="mt-2">
                <div className="field-label">Proposed initiatives</div>
                {s.initiatives!
                  .filter((i) => i.status === 'PROPOSED')
                  .map((i) => (
                    <div key={i.id} className="mb-1 flex items-center justify-between rounded-lg border border-slate-200 px-3 py-1.5 text-sm">
                      <span>
                        {i.title} — <span className="tabular-nums text-slate-500">{scr(i.costing.y1)}</span>
                      </span>
                      <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setInitiativeTarget(i.id)}>
                        Decide
                      </button>
                    </div>
                  ))}
              </div>
            ) : null}
          </div>
        ))
      )}
      {decideTarget ? (
        <DecideLineModal
          lineId={decideTarget.lineId}
          label={decideTarget.label}
          onClose={() => setDecideTarget(null)}
          onDone={() => {
            setDecideTarget(null);
            onDone();
          }}
        />
      ) : null}
      {initiativeTarget ? (
        <DecideInitiativeModal
          initiativeId={initiativeTarget}
          onClose={() => setInitiativeTarget(null)}
          onDone={() => {
            setInitiativeTarget(null);
            onDone();
          }}
        />
      ) : null}
    </div>
  );
}

function DecideLineModal({ lineId, label, onClose, onDone }: { lineId: string; label: string; onClose: () => void; onDone: () => void }) {
  const [decision, setDecision] = useState<'ACCEPT' | 'CUT' | 'QUERY'>('ACCEPT');
  const [note, setNote] = useState('');
  const [revisedAmount, setRevisedAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/budget-prep/lines/${lineId}/decide`, {
        decision,
        note,
        ...(decision === 'CUT' ? { revisedAmount: Number.parseFloat(revisedAmount) } : {}),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Decision failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Decide — ${label}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Decision">
          <select className="field-input" value={decision} onChange={(e) => setDecision(e.target.value as typeof decision)}>
            <option value="ACCEPT">ACCEPT — fund as requested</option>
            <option value="CUT">CUT — fund a revised amount</option>
            <option value="QUERY">QUERY — send back for clarification</option>
          </select>
        </Field>
        {decision === 'CUT' ? (
          <Field label="Revised amount (SCR)">
            <input className="field-input" type="number" min="0" step="0.01" value={revisedAmount} onChange={(e) => setRevisedAmount(e.target.value)} />
          </Field>
        ) : null}
        <Field label="Note">
          <textarea className="field-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || note.length < 3 || (decision === 'CUT' && !revisedAmount)}>
            {busy ? 'Saving…' : 'Save decision'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DecideInitiativeModal({ initiativeId, onClose, onDone }: { initiativeId: string; onClose: () => void; onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const decide = async (decision: 'ACCEPTED' | 'REJECTED') => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/budget-prep/initiatives/${initiativeId}/decide`, { decision });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Decision failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Decide new initiative" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={() => decide('REJECTED')} disabled={busy}>
            Reject
          </button>
          <button className="btn-success" onClick={() => decide('ACCEPTED')} disabled={busy}>
            Accept
          </button>
        </div>
      </div>
    </Modal>
  );
}

function BillExportCard({ cycleId, voteName }: { cycleId: string; voteName: (id: string) => string }) {
  const q = useApiQuery<{ votes: Array<{ voteId: string; total: number }> }>(`/api/v1/budget-prep/cycles/${cycleId}/bill-export`);
  return (
    <div>
      <h2 className="mb-2 text-sm font-bold text-slate-700">Appropriation Bill annex</h2>
      <div className="card overflow-x-auto">
        {q.loading ? (
          <Spinner />
        ) : q.error ? (
          <ErrorNote message={q.error} />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Vote</th>
                <th className="table-head text-right">Enacted total (Y1)</th>
              </tr>
            </thead>
            <tbody>
              {(q.data?.votes ?? []).map((v) => (
                <tr key={v.voteId}>
                  <td className="table-cell text-xs">{voteName(v.voteId)}</td>
                  <td className="table-cell text-right tabular-nums">{scr(v.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-400">
        This annex is shaped exactly like F03's appropriation payload, ready for Treasury to load line for line once enacted.
      </p>
    </div>
  );
}
